import { ErrorBoundary } from '@sentry/react';
import { Stacked } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { unwrappedPlayerMetricsAtom } from '@/atoms/metric/playerMetrics';
import {
  hasRetrievedAllPlayerAggsAtom,
  playerAggsCountAtom,
  playerAggsDataAtom,
  rawPlayerAggsCountAtom,
} from '@/atoms/queries/player/playerAggData';
import { unwrappedVisibleTableColumnsAtom } from '@/atoms/table';
import { GeneralErrorBoundaryFallback } from '@/components/errorBoundary/ErrorBoundaryFallback';
import { RankDataPillLegend } from '@/components/radar/table/RankDataPillLegend';
import { useTableColumns } from '@/hooks/useTableColumns';
import { AggsTable } from './AggsTable';
import { AggsTableErrorFallback } from './AggsTableErrorFallback';
import { TablePaginationWithoutSuspense } from './TablePagination';

const PlayerAggsTableWithData = () => {
  const [{ data, isLoading, isRefetching }] = useAtom(playerAggsDataAtom);
  const visibleColumns = useAtomValue(unwrappedVisibleTableColumnsAtom);
  const hasRetrievedAllPlayerAggs = useAtomValue(hasRetrievedAllPlayerAggsAtom);
  const totalCount = useAtomValue(rawPlayerAggsCountAtom).data;
  useTableColumns(['player', 'player_metric', 'player_total_metric', 'current_team']);

  return (
    <AggsTable
      data={data}
      isLoading={isLoading || isRefetching}
      visibleColumns={visibleColumns}
      entity="player"
      hasRetrievedAllAggs={hasRetrievedAllPlayerAggs}
      withVideo
      metricData={useAtomValue(unwrappedPlayerMetricsAtom)}
      withSelectedRows
      totalCount={totalCount}
    />
  );
};

const PlayerAggsTable = () => (
  <AggsTableErrorFallback>
    <Suspense fallback={<AggsTable isLoading entity="player" />}>
      <PlayerAggsTableWithData />
    </Suspense>
  </AggsTableErrorFallback>
);

export const PlayerAggsTableWithPagination = () => {
  const totalCount = useAtomValue(playerAggsCountAtom);

  return (
    <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
      <Stacked direction="column" gap="medium">
        <PlayerAggsTable />
        <TablePaginationWithoutSuspense dataLengthAtom={rawPlayerAggsCountAtom} />
        <RankDataPillLegend resultsCount={totalCount} />
      </Stacked>
    </ErrorBoundary>
  );
};
